import {Button, ButtonGroup, Spinner} from "react-bootstrap";
import React, {useState, useEffect} from "react";
import "./ToolBox.scss";
import MenuItemComponent from "../../menu/MenuItem";
import {MapViewMode} from "../MapViewTypes";
import {useAppDispatch, useAppSelector} from "../mapSlice";
import {toggleMgrsGrid, setActiveEditTool, setActiveLineSidc, setGraphicAttachment} from "../mapUiSlice";
import {SIDC_AXIS_OF_ADVANCE} from "../tactical/sidc";
import {RootState} from "../../../store";
import {setPageSize, setOrientation, setScale, setGridSize, setShowExportRect, setIsExporting} from "../export/exportSlice";
import {exportMapToPdf} from "../export/exportUtils";
import LayersPanel from "./LayersPanel";

export default function ToolBox({setTool: setTool, currentTool}) {
    const dispatch = useAppDispatch();
    const gridEnabled = useAppSelector((state: RootState) => state.mapUi.mgrsGridEnabled);
    const exportSettings = useAppSelector((state: RootState) => state.export);
    const mapEntities = useAppSelector((state: RootState) => state.map.value);
    const activeEditTool = useAppSelector((state: RootState) => state.mapUi.activeEditTool);
    const activeLineSidc = useAppSelector((state: RootState) => state.mapUi.activeLineSidc);
    const graphicAttachment = useAppSelector((state: RootState) => state.mapUi.graphicAttachment);
    const [showTopoSubmenu, setShowTopoSubmenu] = useState(false);
    const [showExportSubmenu, setShowExportSubmenu] = useState(false);

    // Lines is a nested option of Edit, so the Edit submenu stays open in both
    // modes — otherwise entering Lines would hide the very item that selects it.
    const showEditSubmenu = currentTool === MapViewMode.EDIT || currentTool === MapViewMode.LINES;
    const showLinesSubmenu = currentTool === MapViewMode.LINES;

    useEffect(() => {
        if (currentTool === MapViewMode.EDIT) {
            dispatch(setActiveEditTool('marker'));
        }

        // Lines mode: default to the attack-line symbol.
        if (currentTool === MapViewMode.LINES) {
            dispatch(setActiveLineSidc(SIDC_AXIS_OF_ADVANCE));
        }

        // Handle export mode
        if (currentTool === MapViewMode.EXPORT) {
            setShowExportSubmenu(true);
            dispatch(setShowExportRect(true));
        } else {
            setShowExportSubmenu(false);
            dispatch(setShowExportRect(false));
        }
    }, [currentTool, dispatch]);

    const handleEditClick = () => {
        setTool(MapViewMode.EDIT);
        dispatch(setActiveEditTool('marker'));
    };

    const handleAddMarkerClick = () => {
        // Reachable from Lines mode too, so it must switch the map mode back,
        // not only the active edit tool.
        setTool(MapViewMode.EDIT);
        dispatch(setActiveEditTool('marker'));
    };

    const handleLinesClick = () => {
        setTool(MapViewMode.LINES);
        dispatch(setActiveLineSidc(SIDC_AXIS_OF_ADVANCE));
    };

    const handleTopoClick = () => {
        setTool(MapViewMode.TOPO);
        setShowTopoSubmenu(!showTopoSubmenu);
    };

    const handleGridClick = () => {
        dispatch(toggleMgrsGrid());
    };

    const handleExportClick = () => {
        setTool(MapViewMode.EXPORT);
        setShowExportSubmenu(true);
    };

    const handleLayersClick = () => {
        setTool(currentTool === MapViewMode.LAYERS ? MapViewMode.BROWSE : MapViewMode.LAYERS);
    };

    const handleExportPdf = async () => {
        dispatch(setIsExporting(true));
        try {
            await exportMapToPdf({
                pageSize: exportSettings.pageSize,
                orientation: exportSettings.orientation,
                scale: exportSettings.scale,
                gridSize: exportSettings.gridSize,
                viewportBounds: exportSettings.viewportBounds,
                entities: mapEntities,
                showMgrsGrid: gridEnabled,
            });
        } finally {
            dispatch(setIsExporting(false));
        }
    };

    return (
        <div className={"toolbox m-2"}>
            <ButtonGroup
                vertical>
                <Button
                    variant={showEditSubmenu ? "primary" : "secondary"}
                    onClick={handleEditClick}>
                    <MenuItemComponent label={'Edit'} icon={'pencil'}></MenuItemComponent>
                </Button>
                {showEditSubmenu && (
                    <>
                        <Button
                            variant={currentTool === MapViewMode.EDIT && activeEditTool === 'marker' ? "info" : "light"}
                            className="submenu-item"
                            onClick={handleAddMarkerClick}>
                            <MenuItemComponent label={'Add Marker'} icon={'plus-circle'}></MenuItemComponent>
                        </Button>
                        <Button
                            variant={currentTool === MapViewMode.LINES ? "info" : "light"}
                            className="submenu-item"
                            onClick={handleLinesClick}>
                            <MenuItemComponent label={'Lines'} icon={'arrow-trend-up'}></MenuItemComponent>
                        </Button>
                        {showLinesSubmenu && (
                            <>
                                <Button
                                    variant={activeLineSidc === SIDC_AXIS_OF_ADVANCE ? "info" : "light"}
                                    className="submenu-item submenu-item-nested"
                                    onClick={() => dispatch(setActiveLineSidc(SIDC_AXIS_OF_ADVANCE))}>
                                    <MenuItemComponent label={'Attack line'} icon={'arrow-trend-up'}></MenuItemComponent>
                                </Button>
                                {/* Attachment is deliberately visible state: an arrow that
                                    silently belongs to a unit (and disappears with it) would
                                    be indistinguishable from a standalone one. */}
                                <Button
                                    variant={graphicAttachment ? "info" : "light"}
                                    className="submenu-item submenu-item-nested"
                                    title={graphicAttachment
                                        ? `Next line attaches to the selected ${graphicAttachment.kind} — click it again to detach`
                                        : 'Click a unit or marker on the map to attach the next line to it'}
                                    onClick={() => dispatch(setGraphicAttachment(null))}>
                                    <MenuItemComponent
                                        label={graphicAttachment
                                            ? `Attached to ${graphicAttachment.kind}`
                                            : 'Standalone'}
                                        icon={'link'}
                                    ></MenuItemComponent>
                                </Button>
                            </>
                        )}
                    </>
                )}
                <Button
                    variant={currentTool === MapViewMode.TOPO ? "primary" : "secondary"}
                    onClick={handleTopoClick}>
                    <MenuItemComponent label={'Topo'} icon={'pen-ruler'}></MenuItemComponent>
                </Button>
                {showTopoSubmenu && currentTool === MapViewMode.TOPO && (
                    <>
                        <Button variant="light" className="submenu-item">
                            <MenuItemComponent label={'Ruler'} icon={'ruler'}></MenuItemComponent>
                        </Button>
                        <Button
                            variant={gridEnabled ? "info" : "light"}
                            className="submenu-item"
                            onClick={handleGridClick}>
                            <MenuItemComponent label={'Grid'} icon={'grid'}></MenuItemComponent>
                        </Button>
                    </>
                )}
                <Button
                    variant={currentTool === MapViewMode.EXPORT ? "primary" : "secondary"}
                    onClick={handleExportClick}>
                    <MenuItemComponent label={'Export'} icon={'file-export'}></MenuItemComponent>
                </Button>
                {showExportSubmenu && currentTool === MapViewMode.EXPORT && (
                    <>
                        {/* Page Size */}
                        <Button
                            variant={exportSettings.pageSize === 'A4' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setPageSize('A4'))}>
                            <MenuItemComponent label={'A4'} icon={'file'}></MenuItemComponent>
                        </Button>
                        <Button
                            variant={exportSettings.pageSize === 'A3' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setPageSize('A3'))}>
                            <MenuItemComponent label={'A3'} icon={'file'}></MenuItemComponent>
                        </Button>
                        {/* Orientation */}
                        <Button
                            variant={exportSettings.orientation === 'landscape' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setOrientation('landscape'))}>
                            <MenuItemComponent label={'Landscape'} icon={'image'}></MenuItemComponent>
                        </Button>
                        <Button
                            variant={exportSettings.orientation === 'portrait' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setOrientation('portrait'))}>
                            <MenuItemComponent label={'Portrait'} icon={'image'}></MenuItemComponent>
                        </Button>
                        {/* Scale */}
                        <Button
                            variant={exportSettings.scale === '1:10000' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setScale('1:10000'))}>
                            <MenuItemComponent label={'1:10k'} icon={'ruler-combined'}></MenuItemComponent>
                        </Button>
                        <Button
                            variant={exportSettings.scale === '1:25000' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setScale('1:25000'))}>
                            <MenuItemComponent label={'1:25k'} icon={'ruler-combined'}></MenuItemComponent>
                        </Button>
                        {/* Grid Size */}
                        <Button
                            variant={exportSettings.gridSize === '100m' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setGridSize('100m'))}>
                            <MenuItemComponent label={'100m'} icon={'grid'}></MenuItemComponent>
                        </Button>
                                                <Button
                            variant={exportSettings.gridSize === '1km' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setGridSize('1km'))}>
                            <MenuItemComponent label={'1km'} icon={'grid'}></MenuItemComponent>
                        </Button>
                        <Button
                            variant={exportSettings.gridSize === '100km' ? "info" : "light"}
                            className="submenu-item"
                            onClick={() => dispatch(setGridSize('100km'))}>
                            <MenuItemComponent label={'100km'} icon={'grid'}></MenuItemComponent>
                        </Button>
                        {/* Export Action */}
                        <Button
                            variant="success"
                            className="submenu-item"
                            onClick={handleExportPdf}
                            disabled={exportSettings.isExporting}>
                            {exportSettings.isExporting ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <MenuItemComponent label={'Export PDF'} icon={'download'}></MenuItemComponent>
                            )}
                        </Button>
                    </>
                )}
                <Button
                    variant={currentTool === MapViewMode.LAYERS ? "primary" : "secondary"}
                    onClick={handleLayersClick}>
                    <MenuItemComponent label={'Layers'} icon={'layer-group'}></MenuItemComponent>
                </Button>
            </ButtonGroup>
            {currentTool === MapViewMode.LAYERS && <LayersPanel />}
        </div>
    );
}